import { Box, Link, Tooltip } from "@mui/material"
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react"
import { intl } from "src/internationalization/intl"
import { Button, Chip, CircularProgress, Grid, Typography } from "src/lib"
import { TextInput } from "src/lib/components/basics/TextInput"
import { getMarketRankingDisplayName } from "src/parts/CommitmentsSection/useRankingAssets"
import {
    buildRankingPromptSnippet,
    formatPropertyTypeLabel,
    formatValueHintSummary,
    getPropertyDisplayLabel,
    isEnumHint,
    getOrderingPropertiesForMarket,
    type DateFilterOp,
    type NumericFilterOp,
    type OrderingPropertyMeta,
    type OrderingPropertiesForMarketResponse,
    type RankingPromptInsertOptions,
} from "src/services/api/sorting"

type FilterDraft = {
    propertyName: string
    kind: "numeric" | "date"
    op: NumericFilterOp | DateFilterOp
    value: string
}

type PropertySectionMode = "order" | "filter"

type RankingPropertiesPickerProps = {
    marketKey: string
    marketLabel?: string
    onInsertPrompt: (snippet: string) => void
    variant?: "inline" | "modal"
    onOpenModal?: () => void
}

const propertiesCache = new Map<string, OrderingPropertiesForMarketResponse>()

function isNumericProperty(property: OrderingPropertyMeta): boolean {
    return (
        property.type === "integer" ||
        property.type === "decimal" ||
        property.value_hint?.kind === "numeric_range"
    )
}

function isDateProperty(property: OrderingPropertyMeta): boolean {
    return property.type === "date"
}

function isBooleanProperty(property: OrderingPropertyMeta): boolean {
    return property.type === "boolean" || property.value_hint?.kind === "boolean"
}

type PropertyRowProps = {
    property: OrderingPropertyMeta
    sectionMode: PropertySectionMode
    filterDraft: FilterDraft | null
    filterError: string | null
    isModal: boolean
    onInsert: (property: OrderingPropertyMeta, options?: RankingPromptInsertOptions) => void
    onToggleFilterDraft: (propertyName: string, kind: FilterDraft["kind"], op: FilterDraft["op"]) => void
    onApplyFilter: (property: OrderingPropertyMeta) => void
    onFilterValueChange: (value: string) => void
    onCancelFilter: () => void
    onFilterKeyDown: (
        e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        property: OrderingPropertyMeta
    ) => void
}

const PropertyRow = ({
    property,
    sectionMode,
    filterDraft,
    filterError,
    isModal,
    onInsert,
    onToggleFilterDraft,
    onApplyFilter,
    onFilterValueChange,
    onCancelFilter,
    onFilterKeyDown,
}: PropertyRowProps) => {
    const hintSummary = formatValueHintSummary(property)
    const displayLabel = getPropertyDisplayLabel(property)
    const numeric = isNumericProperty(property)
    const date = isDateProperty(property)
    const booleanProp = isBooleanProperty(property)
    const samples = isEnumHint(property.value_hint) ? property.value_hint.samples : []
    const showOrderActions = sectionMode === "order" && (numeric || date)
    const showFilterActions =
        sectionMode === "filter" && (numeric || date || booleanProp || samples.length > 0)
    const activeDraft = filterDraft?.propertyName === property.name ? filterDraft : null

    return (
        <Box
            sx={{
                width: "100%",
                pb: 0.75,
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                "&:last-of-type": { borderBottom: "none", pb: 0 },
            }}
        >
            <Tooltip
                title={
                    hintSummary
                        ? `${formatPropertyTypeLabel(property.type)} · ${hintSummary}`
                        : formatPropertyTypeLabel(property.type)
                }
                arrow
            >
                <span>
                    <Chip
                        label={displayLabel}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => onInsert(property)}
                        sx={{ cursor: "pointer", fontWeight: 600 }}
                    />
                </span>
            </Tooltip>

            {showOrderActions && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                        pl: 0.25,
                    }}
                >
                    {numeric && (
                        <>
                            <Chip
                                label={intl("rankingPropertyHigher")}
                                size="small"
                                variant="filled"
                                color="neutral"
                                onClick={() =>
                                    onInsert(property, { mode: "direction", direction: "higher" })
                                }
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                            <Chip
                                label={intl("rankingPropertyLower")}
                                size="small"
                                variant="filled"
                                color="neutral"
                                onClick={() =>
                                    onInsert(property, { mode: "direction", direction: "lower" })
                                }
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                        </>
                    )}
                    {date && (
                        <>
                            <Chip
                                label={intl("rankingPropertyNewer")}
                                size="small"
                                variant="filled"
                                color="neutral"
                                onClick={() =>
                                    onInsert(property, { mode: "direction", direction: "newer" })
                                }
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                            <Chip
                                label={intl("rankingPropertyOlder")}
                                size="small"
                                variant="filled"
                                color="neutral"
                                onClick={() =>
                                    onInsert(property, { mode: "direction", direction: "older" })
                                }
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                        </>
                    )}
                </Box>
            )}

            {showFilterActions && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                        pl: 0.25,
                    }}
                >
                    {numeric && (
                        <>
                            <Chip
                                label={intl("rankingPropertyFilterGt")}
                                size="small"
                                variant={
                                    activeDraft?.kind === "numeric" && activeDraft.op === "gt"
                                        ? "filled"
                                        : "outlined"
                                }
                                color="neutral"
                                onClick={() => onToggleFilterDraft(property.name, "numeric", "gt")}
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                            <Chip
                                label={intl("rankingPropertyFilterLt")}
                                size="small"
                                variant={
                                    activeDraft?.kind === "numeric" && activeDraft.op === "lt"
                                        ? "filled"
                                        : "outlined"
                                }
                                color="neutral"
                                onClick={() => onToggleFilterDraft(property.name, "numeric", "lt")}
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                        </>
                    )}
                    {date && (
                        <>
                            <Chip
                                label={intl("rankingPropertyFilterAfter")}
                                size="small"
                                variant={
                                    activeDraft?.kind === "date" && activeDraft.op === "after"
                                        ? "filled"
                                        : "outlined"
                                }
                                color="neutral"
                                onClick={() => onToggleFilterDraft(property.name, "date", "after")}
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                            <Chip
                                label={intl("rankingPropertyFilterBefore")}
                                size="small"
                                variant={
                                    activeDraft?.kind === "date" && activeDraft.op === "before"
                                        ? "filled"
                                        : "outlined"
                                }
                                color="neutral"
                                onClick={() =>
                                    onToggleFilterDraft(property.name, "date", "before")
                                }
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                        </>
                    )}
                    {booleanProp && (
                        <>
                            <Chip
                                label={intl("rankingPropertyYes")}
                                size="small"
                                variant="filled"
                                color="neutral"
                                onClick={() => onInsert(property, { booleanValue: true })}
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                            <Chip
                                label={intl("rankingPropertyNo")}
                                size="small"
                                variant="filled"
                                color="neutral"
                                onClick={() => onInsert(property, { booleanValue: false })}
                                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            />
                        </>
                    )}
                    {samples.map((sample) => (
                        <Chip
                            key={`${property.name}-${sample}`}
                            label={sample}
                            size="small"
                            variant="outlined"
                            color="neutral"
                            onClick={() => onInsert(property, { mode: "sample", sample })}
                            sx={{ cursor: "pointer", fontSize: "0.65rem", maxWidth: "100%" }}
                        />
                    ))}
                </Box>
            )}

            {sectionMode === "filter" && activeDraft && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                        pl: 0.25,
                        width: "100%",
                    }}
                >
                    <TextInput
                        autoComplete="off"
                        size="small"
                        fullWidth={false}
                        placeholder={
                            activeDraft.kind === "date"
                                ? intl("rankingPropertyFilterDatePlaceholder")
                                : intl("rankingPropertyFilterValuePlaceholder")
                        }
                        value={activeDraft.value}
                        onChange={(e) => onFilterValueChange(e.target.value)}
                        onKeyDown={(e) => onFilterKeyDown(e, property)}
                        sx={{ flex: "1 1 100px", minWidth: 80, maxWidth: 160 }}
                        inputProps={{
                            inputMode: activeDraft.kind === "date" ? "text" : "decimal",
                        }}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => onApplyFilter(property)}
                    >
                        {intl("rankingPropertyFilterApply")}
                    </Button>
                    <Button size="small" variant="text" color="primary" onClick={onCancelFilter}>
                        {intl("rankingPropertyFilterCancel")}
                    </Button>
                    {filterError && (
                        <Typography variant="caption" sx={{ color: "error.main", width: "100%" }}>
                            {filterError}
                        </Typography>
                    )}
                </Box>
            )}

            {isModal && hintSummary && (
                <Typography
                    variant="caption"
                    color="secondary"
                    sx={{ display: "block", mt: 0.35, pl: 0.25 }}
                >
                    {formatPropertyTypeLabel(property.type)}
                    {hintSummary ? ` · ${hintSummary}` : ""}
                </Typography>
            )}
        </Box>
    )
}

type PropertySectionProps = {
    title: string
    properties: OrderingPropertyMeta[]
    sectionMode: PropertySectionMode
    filterDraft: FilterDraft | null
    filterError: string | null
    isModal: boolean
    onInsert: (property: OrderingPropertyMeta, options?: RankingPromptInsertOptions) => void
    onToggleFilterDraft: (propertyName: string, kind: FilterDraft["kind"], op: FilterDraft["op"]) => void
    onApplyFilter: (property: OrderingPropertyMeta) => void
    onFilterValueChange: (value: string) => void
    onCancelFilter: () => void
    onFilterKeyDown: (
        e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        property: OrderingPropertyMeta
    ) => void
}

const PropertySection = ({
    title,
    properties,
    sectionMode,
    filterDraft,
    filterError,
    isModal,
    onInsert,
    onToggleFilterDraft,
    onApplyFilter,
    onFilterValueChange,
    onCancelFilter,
    onFilterKeyDown,
}: PropertySectionProps) => {
    if (properties.length === 0) return null
    return (
        <Box sx={{ mb: 1.5, "&:last-of-type": { mb: 0 } }}>
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75, display: "block" }}
            >
                {title}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {properties.map((property) => (
                    <PropertyRow
                        key={`${sectionMode}-${property.name}`}
                        property={property}
                        sectionMode={sectionMode}
                        filterDraft={filterDraft}
                        filterError={filterError}
                        isModal={isModal}
                        onInsert={onInsert}
                        onToggleFilterDraft={onToggleFilterDraft}
                        onApplyFilter={onApplyFilter}
                        onFilterValueChange={onFilterValueChange}
                        onCancelFilter={onCancelFilter}
                        onFilterKeyDown={onFilterKeyDown}
                    />
                ))}
            </Box>
        </Box>
    )
}

const RankingPropertiesPicker = ({
    marketKey,
    marketLabel,
    onInsertPrompt,
    variant = "inline",
    onOpenModal,
}: RankingPropertiesPickerProps) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [data, setData] = useState<OrderingPropertiesForMarketResponse | null>(null)
    const [filterDraft, setFilterDraft] = useState<FilterDraft | null>(null)
    const [filterError, setFilterError] = useState<string | null>(null)
    const mountedRef = useRef(true)

    const toggleFilterDraft = (
        propertyName: string,
        kind: FilterDraft["kind"],
        op: FilterDraft["op"]
    ) => {
        setFilterError(null)
        setFilterDraft((prev) =>
            prev?.propertyName === propertyName && prev.kind === kind && prev.op === op
                ? null
                : {
                      propertyName,
                      kind,
                      op,
                      value: prev?.propertyName === propertyName ? prev.value : "",
                  }
        )
    }

    const applyFilter = (property: OrderingPropertyMeta) => {
        if (!filterDraft || filterDraft.propertyName !== property.name) return
        const val = filterDraft.value.trim()
        if (!val) {
            setFilterError(
                filterDraft.kind === "date"
                    ? intl("rankingPropertyFilterInvalidDate")
                    : intl("rankingPropertyFilterInvalidValue")
            )
            return
        }
        if (filterDraft.kind === "numeric") {
            const normalized = val.replace(",", ".")
            if (!Number.isFinite(Number(normalized))) {
                setFilterError(intl("rankingPropertyFilterInvalidValue"))
                return
            }
            insert(property, {
                mode: "filter",
                filterOp: filterDraft.op as NumericFilterOp,
                filterValue: normalized,
            })
        } else {
            insert(property, {
                mode: "filter",
                filterOp: filterDraft.op as DateFilterOp,
                filterValue: val,
            })
        }
        setFilterDraft(null)
        setFilterError(null)
    }

    const handleFilterKeyDown = (
        e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        property: OrderingPropertyMeta
    ) => {
        if (e.key === "Enter") {
            e.preventDefault()
            applyFilter(property)
        }
        if (e.key === "Escape") {
            setFilterDraft(null)
            setFilterError(null)
        }
    }

    const load = useCallback(async () => {
        const key = marketKey.trim()
        if (!key) return
        const cached = propertiesCache.get(key.toUpperCase())
        if (cached) {
            setData(cached)
            setError(null)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const res = await getOrderingPropertiesForMarket(key)
            propertiesCache.set(key.toUpperCase(), res)
            if (mountedRef.current) setData(res)
        } catch {
            if (mountedRef.current) {
                setData(null)
                setError(intl("rankingPropertiesLoadError"))
            }
        } finally {
            if (mountedRef.current) setLoading(false)
        }
    }, [marketKey])

    useEffect(() => {
        mountedRef.current = true
        load()
        return () => {
            mountedRef.current = false
        }
    }, [load])

    const insert = (property: OrderingPropertyMeta, options?: RankingPromptInsertOptions) => {
        onInsertPrompt(buildRankingPromptSnippet(property, options))
    }

    const isModal = variant === "modal"
    const ordering = data?.ordering ?? []
    const filtering = data?.filtering ?? []
    const isEmpty = ordering.length === 0 && filtering.length === 0
    const title =
        marketLabel ?? getMarketRankingDisplayName(data?.market || marketKey) ?? marketKey

    if (loading) {
        return (
            <Grid container justifyContent="center" sx={{ py: isModal ? 2 : 1 }}>
                <CircularProgress color="primary" size={22} />
            </Grid>
        )
    }

    if (error) {
        return (
            <Typography variant="body2" sx={{ color: "error.main" }}>
                {error}
            </Typography>
        )
    }

    if (isEmpty) {
        return (
            <Typography variant="body2" color="secondary">
                {intl("rankingPropertiesEmpty")}
            </Typography>
        )
    }

    return (
        <Box>
            {isModal && (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {title}
                </Typography>
            )}
            {!isModal && (
                <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Typography variant="caption" color="secondary">
                        {intl("rankingPropertiesClickHint")}
                    </Typography>
                    {onOpenModal && (
                        <Link
                            component="button"
                            type="button"
                            variant="caption"
                            underline="hover"
                            onClick={onOpenModal}
                            sx={{ cursor: "pointer", fontWeight: 600 }}
                        >
                            {intl("rankingPropertiesExpandModal")}
                        </Link>
                    )}
                </Grid>
            )}
            <Box
                sx={{
                    maxHeight: isModal ? "none" : 280,
                    overflowY: isModal ? "visible" : "auto",
                    pr: 0.5,
                }}
            >
                <PropertySection
                    title={intl("rankingPropertiesOrderingTitle")}
                    properties={ordering}
                    sectionMode="order"
                    filterDraft={filterDraft}
                    filterError={filterError}
                    isModal={isModal}
                    onInsert={insert}
                    onToggleFilterDraft={toggleFilterDraft}
                    onApplyFilter={applyFilter}
                    onFilterValueChange={(value) => {
                        if (!filterDraft) return
                        setFilterDraft({ ...filterDraft, value })
                        setFilterError(null)
                    }}
                    onCancelFilter={() => {
                        setFilterDraft(null)
                        setFilterError(null)
                    }}
                    onFilterKeyDown={handleFilterKeyDown}
                />
                <PropertySection
                    title={intl("rankingPropertiesFilteringTitle")}
                    properties={filtering}
                    sectionMode="filter"
                    filterDraft={filterDraft}
                    filterError={filterError}
                    isModal={isModal}
                    onInsert={insert}
                    onToggleFilterDraft={toggleFilterDraft}
                    onApplyFilter={applyFilter}
                    onFilterValueChange={(value) => {
                        if (!filterDraft) return
                        setFilterDraft({ ...filterDraft, value })
                        setFilterError(null)
                    }}
                    onCancelFilter={() => {
                        setFilterDraft(null)
                        setFilterError(null)
                    }}
                    onFilterKeyDown={handleFilterKeyDown}
                />
            </Box>
        </Box>
    )
}

export { RankingPropertiesPicker, propertiesCache as rankingPropertiesCache }
